import { For, Show, createMemo, createSignal, type Component } from 'solid-js';
import { Portal } from 'solid-js/web';
import { Text } from '~ui/text';
import { formatCurrency, formatMonth, formatNumber } from '~/lib/format';
import { monthlySeries } from '~/stores/dividend-store';
import { clearHover, hoverAt, setHoverAt } from '~/stores/crosshair-store';

// What the book actually paid, month by month.
//
// Bars, not a line. A line says the quantity between two points is real and
// moving — which is true of a price and false of this: nothing was paid on the
// 14th, there is one number per month and the months are separate events. A bar
// per month says exactly that, and the height is the money.
//
// Drawn as HTML rather than in the SVG the other charts use. Those stretch a
// 100×40 viewBox to fit, which is harmless for a line and wrong for bars: the
// gap between them would stretch with everything else, so a wide pane would
// have fat gaps and a narrow one none. Divs in a flex row keep a 1px seam at
// any width, and square edges on the pixel grid.
//
// Zero-based, and unavoidably so now — a bar starts at zero by construction.
// That was the one thing the line version had to be argued into.

/** Above this, the bars are thinner than the seam between them and the chart
    reads as a comb. The seam goes rather than the bars. */
const SEAM_UP_TO = 90;

/** The record, for a caller with a header to put it in — same arrangement as the
    distribution chart's summary. */
export const paymentsSummary = (): string | null => {
    const months = monthlySeries();
    if (months.length === 0) return null;
    const total = months.reduce((sum, m) => sum + m.amount, 0);
    return `${months.length} months · ${formatCurrency(total / months.length)} avg`;
};

export const PaymentsChart: Component<{ height?: string }> = (props) => {
    const months = createMemo(() => monthlySeries());
    const hi = createMemo(() => Math.max(...months().map((m) => m.amount), 0) || 1);

    const [over, setOver] = createSignal(false);
    const [cursor, setCursor] = createSignal({ x: 0, y: 0 });

    /**
     * The shared date, snapped to a month.
     *
     * This chart's x is a month index, not a continuous time axis, so the
     * highlight steps between months while a price chart's crosshair slides.
     * That is honest: between two months there is no third total to point at.
     */
    const hover = (): number | null => {
        const t = hoverAt();
        const all = months();
        if (t == null || all.length === 0) return null;
        let found: number | null = null;
        for (let i = 0; i < all.length; i += 1) {
            if (Date.parse(`${all[i].period}-01T00:00:00`) > t) break;
            found = i;
        }
        return found;
    };
    const at = () => (hover() == null ? null : (months()[hover()!] ?? null));

    const track = (e: MouseEvent & { currentTarget: HTMLDivElement }) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const count = months().length;
        if (rect.width <= 0 || count === 0) return;
        // No inset to invert: the bars fill the box edge to edge, so the column
        // under the cursor is the fraction across times the count.
        const fraction = Math.min(0.999, Math.max(0, (e.clientX - rect.left) / rect.width));
        const month = months()[Math.floor(fraction * count)];
        // Published as a date, which is the only thing this chart could share
        // with another one on the page.
        if (month) setHoverAt(Date.parse(`${month.period}-01T00:00:00`));
        setCursor({ x: e.clientX, y: e.clientY });
        setOver(true);
    };

    return (
        <Show
            when={months().length > 0}
            fallback={
                <div class='flex min-h-0 flex-1 items-center justify-center'>
                    <Text tone='muted'>No payments received yet</Text>
                </div>
            }>
            {/* Padded rather than bled to the cell edges. Flush, the tallest
                bar touched the title rule above it and the first and last
                touched the seams either side, so the plot read as a texture
                filling the cell rather than as a figure inside one. The
                handlers are on the inner box, not this one, so the column under
                the cursor is measured against the bars and not against the
                padding. */}
            <div class={`w-full px-3 py-3 ${props.height ?? 'h-44'}`}>
                <div
                    class={`flex h-full w-full items-end ${months().length <= SEAM_UP_TO ? 'gap-px' : ''}`}
                    onMouseMove={track}
                    onMouseLeave={() => {
                        setOver(false);
                        clearHover();
                    }}>
                    <For each={months()}>
                        {(m, i) => (
                            // The column is the hit area and the highlight; the
                            // bar inside it is the data. Highlighting the bar
                            // itself would leave a month that paid nothing with
                            // nothing to point at, which is exactly the month
                            // worth pointing at.
                            <div class={`flex h-full min-w-0 flex-1 items-end ${hover() === i() ? 'bg-muted' : ''}`}>
                                {/* Green, like every other figure in this app
                                    for money coming in. The line version was
                                    monochrome because a line carries its meaning
                                    in its path; a bar is a block of colour and
                                    may as well be the right one. */}
                                <div class='w-full bg-green' style={{ height: `${((m.amount / hi()) * 100).toFixed(2)}%` }} />
                            </div>
                        )}
                    </For>
                </div>
            </div>

            {/* Instant, and at the pointer. `title` waits about a second, which
                on a chart where the whole plot is the hit area reads as nothing
                having happened. Portalled and fixed because Panel is
                overflow-hidden and would clip it. */}
            <Show when={over() && at()}>
                <Portal>
                    <div
                        class='pointer-events-none fixed z-[200] rounded border bg-background px-2 py-1 text-foreground shadow'
                        style={{ left: `${Math.min(cursor().x + 12, window.innerWidth - 220)}px`, top: `${Math.min(cursor().y + 12, window.innerHeight - 64)}px` }}>
                        <div class='flex items-baseline gap-2 whitespace-nowrap'>
                            <Text tone='muted'>{formatMonth(at()!.period)}</Text>
                            <Text tone={at()!.amount > 0 ? 'green' : 'muted'} weight='medium'>
                                {formatCurrency(at()!.amount)}
                            </Text>
                        </div>
                        <Text tone='muted' class='block whitespace-nowrap'>
                            {at()!.payments === 0 ? 'nothing paid' : `${formatNumber(at()!.payments)} ${at()!.payments === 1 ? 'payment' : 'payments'}`}
                        </Text>
                    </div>
                </Portal>
            </Show>
        </Show>
    );
};

export default PaymentsChart;
